import type { AxleListing } from '@contracts/axle/AxleListing';
import type { TrailerWithAxlesResponse } from '@contracts/trailer/TrailerWithAxlesResponse';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import { Button } from '@src/common/components/button/Button';
import { IconButton } from '@src/common/components/IconButton';
import { MenuItem, Select } from '@src/common/components/Select';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { prefixFieldNameIfPrefixExists } from '@src/common/utils/formUtils';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import type { ComposedFormEntries, FormEntries } from '@src/orders/FormEntries';
import { useClient } from '@src/orders/hooks/useClient';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { fillAxleInformation } from '@src/orders/PermitFormSections/axlesConfiguration/FillAxleInformation';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { ControllerFormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import { AddTrailerDrawer } from '@src/orders/PermitFormSections/haulingEquipment/AddTrailerDrawer';
import { EditTrailerDrawer } from '@src/orders/PermitFormSections/haulingEquipment/EditTrailerDrawer';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import { trailerFields } from '@src/orders/schemas/TrailerInformationInputsSchema';
import { AxleFormName } from '@src/permitField/AxleInformationFields';
import {
  TrailerDeckHeightField,
  TrailerEmptyWeightField,
  TrailerGroundClearanceField,
  TrailerHeightField,
  TrailerKingpinSetbackLengthField,
  TrailerKingpinSettingLengthField,
  TrailerLengthField,
  TrailerRegisteredWeightField,
  TrailerWheelbaseLengthField,
  TrailerWidthField,
} from '@src/permitField/TrailerDimensionFields';
import {
  IsDualLaneField,
  IsTrunnionAxleField,
  TrailerAxleCountField,
  TrailerCountFieldName,
  TrailerFormUtils,
  TrailerIdField,
  TrailerLicensePlateField,
  TrailerLicensePlateStateField,
  TrailerLicensePlateTypeField,
  TrailerMakeField,
  TrailersFormName,
  TrailerTypeField,
  TrailerUnitNumberField,
  TrailerVINField,
  TrailerYearField,
} from '@src/permitField/TrailerInformationFields';
import { TrailerForm } from '@src/trailer/TrailerForm';
import type { TrailerFormSchema } from '@src/trailer/TrailerFormSchema';
import { newTrailerKey, TrailerSelector } from '@src/trailer/TrailerSelector';
import { useId, useRef, useState } from 'react';
import type { Control, FieldPath } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import type { UseFormSetValue } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

const _fields = [
  IsDualLaneField.fieldName,
  IsTrunnionAxleField.fieldName,
  TrailersFormName,
] as const;

export type TrailerInformationFormEntries = FormEntries<string[]>;

type TrailersInformation = ComposedFormEntries<typeof _fields, string[]>;

type Props = {
  clientId: string;
  fields: string[];
  axleFields: string[];
} & CollapseListener;

const emptyTrailer = {
  id: '',
  unitNumber: '',
  make: '',
  type: '',
  vin: '',
  year: 0,
  axles: [],
  licensePlateNumber: '',
  licensePlateState: '',
  licensePlateType: null,
  length: null,
  width: null,
  height: null,
  wheelbaseLength: null,
  deckHeight: null,
  groundClearance: null,
  kingpinSetbackLength: null,
  kingpinSettingLength: null,
  emptyWeight: null,
  registeredWeight: null,
};

export const TrailerInformationSection = ({ clientId, ...props }: Props) => {
  const { t } = useTranslation();

  const {
    control,
    unregister,
    getValues,
    setValue,
    register,
    formState: { disabled: formDisabled },
  } = useFormContext<TrailersInformation>();
  const { isFieldDisabled } = useDisabledFields();

  const [trailerCount, setTrailerCount] = useState<number>(
    getValues('trailers')
      ? (getValues('trailers') as Array<unknown>).length
      : 0,
  );

  const updateTrailerCount = (newCount: number) => {
    if (newCount === 0) {
      unregister(TrailersFormName);
      register(TrailersFormName);
      setValue(TrailersFormName, []);
      setTrailerCount(newCount);
      return;
    }

    if (newCount < trailerCount) {
      const existingTrailerInformation = getValues(
        TrailersFormName,
      ) as TrailerInformationFormEntries[];
      for (let i = trailerCount - 1; i >= newCount; i--) {
        unregister(`${TrailersFormName}.${i}`);
        existingTrailerInformation.pop();
      }
      setValue(TrailersFormName, existingTrailerInformation);
    }

    setTrailerCount(newCount);
  };
  const fieldId = useId();

  if (props.fields.length <= 0) {
    return null;
  }

  const hasAtLeastOneFieldDisabled = props.fields.some(isFieldDisabled);

  return (
    <Stack gap={theme.spacing['3']} aria-labelledby="trailerInformation">
      <Typography component="h2" variant="h5" id="trailerInformation">
        {t('trailerInformation')}
      </Typography>

      <Stack direction="column" gap={theme.spacing['3']}>
        <Select
          disabled={formDisabled || hasAtLeastOneFieldDisabled}
          value={trailerCount}
          className={fullWidth}
          id={fieldId}
          label={t(`fields.${TrailerCountFieldName}`)}
          onChange={(e) =>
            updateTrailerCount(parseInt(e.target.value as string))
          }
        >
          {Array.from({ length: 6 }, (_, i: number) => i).map((i) => (
            <MenuItem key={`${fieldId}-${i}`} value={i}>
              {i}
            </MenuItem>
          ))}
        </Select>
        {trailerCount > 0 && (
          <Stack direction="row" gap={theme.spacing['3']}>
            <ConditionalField
              fields={props.fields}
              conditions={IsDualLaneField.fieldName}
            >
              <ControllerFormCheckbox
                control={control}
                fieldName={IsDualLaneField.fieldName}
                disabled={isFieldDisabled(IsDualLaneField.fieldName)}
              />
            </ConditionalField>
            <ConditionalField
              fields={props.fields}
              conditions={IsTrunnionAxleField.fieldName}
            >
              <ControllerFormCheckbox
                control={control}
                fieldName={IsTrunnionAxleField.fieldName}
                disabled={isFieldDisabled(IsDualLaneField.fieldName)}
              />
            </ConditionalField>
          </Stack>
        )}
        {Array.from({ length: trailerCount }, (_, i) => i).map((i) => (
          <TrailerSectionForm
            axleFields={props.axleFields}
            trailerIndex={i}
            clientId={clientId}
            collapseEventName={props.collapseEventName}
            uncollapseEventName={props.uncollapseEventName}
            key={i}
            fields={props.fields}
          />
        ))}
      </Stack>
    </Stack>
  );
};

interface TrailerFormProps {
  trailerIndex: number;
  clientId: string;
  fields: string[];
  axleFields: string[];
}

const TrailerSectionForm = ({
  trailerIndex,
  clientId,
  collapseEventName,
  uncollapseEventName,
  fields,
  axleFields,
}: TrailerFormProps & CollapseListener) => {
  const { t } = useTranslation();

  const { sectionInError } = useSectionError(
    Object.keys(trailerFields).map((f) => `${f}.${trailerIndex}`),
  );

  if (fields.length <= 0) {
    return null;
  }

  return (
    <EditOrderCollapsible
      key={`specificTrailerSection-${trailerIndex}`}
      title={`${t('trailer')} ${trailerIndex + 1}`}
      id={`specificTrailerSection-${trailerIndex}`}
      collapseEventName={collapseEventName}
      uncollapseEventName={uncollapseEventName}
      inError={sectionInError}
    >
      <IndexedTrailerForm
        clientId={clientId}
        trailerIndex={trailerIndex}
        fields={fields}
        axleFields={axleFields}
      />
    </EditOrderCollapsible>
  );
};

interface IndexedTrailerFormProps {
  trailerIndex: number;
  clientId: string;
  fields: string[];
  axleFields: string[];
}

const IndexedTrailerForm = ({
  trailerIndex,
  clientId,
  fields,
  axleFields,
}: IndexedTrailerFormProps) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { disabled: formDisabled },
  } = useFormContext<Record<string, unknown>>();
  const trailerFieldPrefix =
    trailerIndex !== undefined
      ? `${TrailersFormName}.${trailerIndex}`
      : undefined;

  const selectedTrailerId = watch(
    prefixFieldNameIfPrefixExists(TrailerIdField.fieldName, trailerFieldPrefix),
  );

  const [addTrailerDrawerOpen, setAddTrailerDrawerOpen] = useState(false);
  const [editTrailerDrawerOpen, setEditTrailerDrawerOpen] = useState(false);
  const openAddTrailerModal = () => setAddTrailerDrawerOpen(true);
  const { client, isLoading: isLoadingClient } = useClient({
    id: clientId,
  });

  const {
    current: [unitNumber, id],
  } = useRef(
    getValues([
      prefixFieldNameIfPrefixExists<TrailersInformation>(
        TrailerUnitNumberField.fieldName,
        trailerFieldPrefix,
      ),
      prefixFieldNameIfPrefixExists<TrailersInformation>(
        TrailerIdField.fieldName,
        trailerFieldPrefix,
      ),
    ]),
  );

  const previousTrailerValues = {
    unitNumber,
    id,
  };

  const [isTrailerSelected, setIsTrailerSelected] = useState<boolean>(
    Boolean(selectedTrailerId && selectedTrailerId !== newTrailerKey),
  );

  const selectTrailer = (
    selectedTrailer: TrailerWithAxlesResponse | undefined,
  ) => {
    removeAxles(setValue, trailerIndex);
    if (selectedTrailer) {
      fillTrailerSection(trailerIndex, setValue, selectedTrailer, [
        ...fields,
        AxleFormName,
      ]);
      updateAxlesValues(
        setValue,
        selectedTrailer.axles,
        axleFields,
        TrailerFormUtils.pathToTrailerAxles(trailerIndex),
      );
      setIsTrailerSelected(true);
    } else {
      //Temporary trailer have been selected
      fillTrailerSection(trailerIndex, setValue, emptyTrailer, [
        ...fields,
        AxleFormName,
      ]);
      updateAxlesValues(
        setValue,
        [],
        axleFields,
        TrailerFormUtils.pathToTrailerAxles(trailerIndex),
      );
      setIsTrailerSelected(false);
    }
  };

  if (fields.length <= 0) {
    return null;
  }

  return (
    <Stack gap={theme.spacing['3']}>
      <Stack gap={theme.spacing['3']}>
        <Stack direction="row" gap={theme.spacing['3']} alignItems="center">
          <Controller
            control={control}
            name={prefixFieldNameIfPrefixExists<TrailersInformation>(
              TrailerIdField.fieldName,
              trailerFieldPrefix,
            )}
            render={({ field, fieldState: { error } }) => (
              <TrailerSelector
                disabled={
                  formDisabled || isFieldDisabled(TrailerIdField.fieldName)
                }
                clientId={clientId}
                error={error}
                defaultValue={
                  previousTrailerValues.id
                    ? {
                        label: (
                          previousTrailerValues.unitNumber ??
                          previousTrailerValues.id
                        ).toString(),
                        value: previousTrailerValues.id.toString(),
                      }
                    : undefined
                }
                onTrailerSelect={selectTrailer}
                {...field}
              />
            )}
          />
          {(selectedTrailerId as string) &&
            selectedTrailerId !== newTrailerKey &&
            !formDisabled && (
              <>
                <IconButton
                  disabled={isFieldDisabled(TrailerIdField.fieldName)}
                  onClick={() => setEditTrailerDrawerOpen(true)}
                  title={t('editTrailer')}
                >
                  <Edit />
                </IconButton>
              </>
            )}
        </Stack>
        {!formDisabled && (
          <Button
            disabled={isFieldDisabled(TrailerIdField.fieldName)}
            onClick={openAddTrailerModal}
            endIcon={<Add />}
          >
            {t('addTrailer')}
          </Button>
        )}

        {!isLoadingClient && client && (
          <>
            <AddTrailerDrawer
              isOpen={addTrailerDrawerOpen}
              onClose={() => setAddTrailerDrawerOpen(false)}
              client={client}
              onAdd={(newTrailer) => {
                setValue(
                  prefixFieldNameIfPrefixExists<TrailersInformation>(
                    TrailerIdField.fieldName,
                    trailerFieldPrefix,
                  ),
                  newTrailer.id,
                );
                setAddTrailerDrawerOpen(false);
                fillTrailerSection(trailerIndex, setValue, newTrailer, fields);
              }}
            />
            {selectedTrailerId && selectedTrailerId !== newTrailerKey && (
              <EditTrailerDrawer
                trailerId={selectedTrailerId.toString()}
                isOpen={editTrailerDrawerOpen}
                onClose={() => setEditTrailerDrawerOpen(false)}
                client={client}
                onUpdate={(updatedTrailer) => {
                  setEditTrailerDrawerOpen(false);
                  fillTrailerSection(
                    trailerIndex,
                    setValue,
                    updatedTrailer,
                    fields,
                  );
                  updateAxlesValues(
                    setValue,
                    updatedTrailer.axles,
                    axleFields,
                    TrailerFormUtils.pathToTrailerAxles(trailerIndex),
                  );
                }}
              />
            )}
          </>
        )}
      </Stack>

      <TrailerForm
        readonly={isTrailerSelected}
        control={
          control as unknown as Control<yup.InferType<typeof TrailerFormSchema>>
        }
        trailerIndex={trailerIndex}
        fields={fields}
      />
    </Stack>
  );
};

const removeAxles = (
  setValue: UseFormSetValue<Record<string, unknown>>,
  trailerIndex: number,
) => {
  setValue(TrailerFormUtils.pathToTrailerAxles(trailerIndex), []);
};

const updateAxlesValues = (
  setValue: UseFormSetValue<Record<string, unknown>>,
  allAxles: AxleListing[],
  fields: string[],
  fieldPrefix: string,
) => {
  allAxles.forEach((axle, i) => {
    fillAxleInformation(i, axle, setValue, fieldPrefix, fields);
  });
};

const fullWidth = css({
  flex: 1,
});
const calculatedFields: string[] = [TrailerAxleCountField.fieldName];
const fieldMappings = {
  unitNumber: TrailerUnitNumberField.fieldName,
  make: TrailerMakeField.fieldName,
  type: TrailerTypeField.fieldName,
  vin: TrailerVINField.fieldName,
  year: TrailerYearField.fieldName,
  axles: AxleFormName,
  licensePlateNumber: TrailerLicensePlateField.fieldName,
  licensePlateState: TrailerLicensePlateStateField.fieldName,
  licensePlateType: TrailerLicensePlateTypeField.fieldName,
  length: TrailerLengthField.fieldName,
  width: TrailerWidthField.fieldName,
  height: TrailerHeightField.fieldName,
  kingpinSetbackLength: TrailerKingpinSetbackLengthField.fieldName,
  kingpinSettingLength: TrailerKingpinSettingLengthField.fieldName,
  wheelbaseLength: TrailerWheelbaseLengthField.fieldName,
  emptyWeight: TrailerEmptyWeightField.fieldName,
  groundClearance: TrailerGroundClearanceField.fieldName,
  deckHeight: TrailerDeckHeightField.fieldName,
  registeredWeight: TrailerRegisteredWeightField.fieldName,
};

const fillTrailerSection = (
  trailerIndex: number,
  setValue: UseFormSetValue<Record<string, unknown>>,
  values: TrailerWithAxlesResponse,
  fields: string[],
) => {
  Object.entries(fieldMappings)
    .filter(([_, fieldName]) => !calculatedFields.includes(fieldName))
    .filter(
      ([_, fieldName]) => fields.includes(fieldName) || fieldName === 'axles',
    )
    .forEach(([key, fieldName]) => {
      if (key === AxleFormName) {
        // TODO Is it still necessary ?
        const value = values[key] ?? '1';
        setValue(
          `${TrailersFormName}.${trailerIndex}.${TrailerAxleCountField.fieldName}` as FieldPath<
            yup.InferType<typeof TrailerFormSchema>
          >,
          value.length.toString(),
          {
            shouldValidate: true,
          },
        );
      } else {
        const value = (values[key as keyof typeof values] ?? '') as
          | string
          | number;
        setValue(
          `${TrailersFormName}.${trailerIndex}.${fieldName}` as FieldPath<
            yup.InferType<typeof TrailerFormSchema>
          >,
          value,
          {
            shouldValidate: true,
          },
        );
      }
    });
};
